/**
 * Customer-side line pricing for the trade builder.
 *
 * A customer line either FOLLOWS the trade's "Customer % of Market" — valued at market × %, with
 * `metadata.pricing.percentOfMarket` saying which % — or is LOCKED at a hand-set price
 * (`metadata.pricing.manualOverride`). PATCH bulk-percent reprices only following lines that carry
 * `marketValueCents`, so every path that adds or reprices a customer line builds its pricing
 * here: a line added without it sat at 100% of market and the global % never reached it.
 *
 * All amounts are base-currency (USD) cents. Market values come from the catalogue price
 * readers; nothing here invents a price.
 *
 * A following line under the store's bulk floor is worth the floor's flat amount instead
 * (`floorApplied`). The SERVER prices following lines when they are saved (trade-pricing.ts); the
 * values built here are the preview the modal shows and what is sent, and the page then shows
 * what the server stored.
 */
import { customerLineValueFromBulkPercent } from './tradeMappers'
import { priceFollowingCustomerLine, type BulkFloorUsd } from '../../../utils/tradeRules'

export type CustomerLinePricing = {
  marketValueCents?: number
  percentOfMarket?: number
  manualOverride?: boolean
  /** Set by the server when the store's bulk floor priced the line. */
  floorApplied?: boolean
}

/** The UI's cap for the trade-level % (the server accepts up to 1000). */
export const CUSTOMER_PERCENT_UI_MAX = 100

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** Drop undefined keys, so a cleared field is absent from the JSON rather than `undefined`. */
function compactPricing(pricing: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(pricing).filter(([, v]) => v !== undefined))
}

/** The "Customer % of Market" remembered on a draft trade, or null when none is set. */
export function readTradeCustomerPercent(
  trade: { metadata?: Record<string, unknown> | null } | null | undefined,
): number | null {
  const raw = trade?.metadata?.customerPercentOfMarket
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null
  return Math.max(0, Math.min(CUSTOMER_PERCENT_UI_MAX, raw))
}

/** Where the draft's % came from ('rules' or 'manual'), or null for a draft saved before sources existed. */
export function readTradeCustomerPercentSource(
  trade: { metadata?: Record<string, unknown> | null } | null | undefined,
): 'rules' | 'manual' | null {
  const raw = trade?.metadata?.customerPercentSource
  return raw === 'rules' || raw === 'manual' ? raw : null
}

/** A line's `metadata.pricing`, or an empty object. */
export function readLinePricing(metadata: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const pricing = metadata?.pricing
  return isRecord(pricing) ? pricing : {}
}

/** The market value a line was added at (USD cents), or 0 when it has none. */
export function readLineMarketCents(metadata: Record<string, unknown> | null | undefined): number {
  const market = readLinePricing(metadata).marketValueCents
  return typeof market === 'number' && market > 0 ? market : 0
}

/**
 * Price a customer line being added.
 * - Hand-set (`manualOverride`): kept as entered, with the % the user typed if they typed one.
 * - Otherwise, with a trade-level % and a market value: valued at that % of market.
 * - Otherwise: the suggested value (the catalogue's price for the chosen variant).
 */
export function priceNewCustomerLine(input: {
  suggestedValueCents: number
  marketValueCents: number
  globalPercent: number | null
  manualOverride?: boolean
  manualPercent?: number | null
  /** The store's bulk floor in USD cents (GET /trades/rules), when on. */
  floor?: BulkFloorUsd | null
}): { valueCents: number; pricing: CustomerLinePricing } {
  const market = input.marketValueCents > 0 ? input.marketValueCents : undefined
  if (input.manualOverride) {
    return {
      valueCents: Math.round(input.suggestedValueCents),
      pricing: compactPricing({
        marketValueCents: market,
        percentOfMarket: typeof input.manualPercent === 'number' ? input.manualPercent : undefined,
        manualOverride: true,
      }),
    }
  }
  if (market && input.globalPercent !== null) {
    const priced = priceFollowingCustomerLine(market, Math.max(0, Math.min(1000, input.globalPercent)), input.floor)
    return {
      valueCents: priced.unitValueCents,
      pricing: compactPricing({
        marketValueCents: market,
        percentOfMarket: input.globalPercent,
        floorApplied: priced.floorApplied ? true : undefined,
      }),
    }
  }
  return {
    valueCents: Math.round(input.suggestedValueCents),
    pricing: compactPricing({ marketValueCents: market }),
  }
}

/** A repricing of one line, as chosen in the inline editor. */
export type LinePriceEdit =
  | { kind: 'amount'; valueCents: number }
  | { kind: 'percent'; percent: number }
  | { kind: 'followGlobal'; globalPercent: number | null; floor?: BulkFloorUsd | null }

/** Body for PATCH /trades/:id/items/:itemId. `metadata` replaces the line's metadata, so it is merged here. */
export type LinePriceUpdate = { valueCents: number; metadata?: Record<string, unknown> }

/**
 * Turn an inline price edit into the PATCH body, or null when it cannot be applied (a % on a line
 * with no market value).
 *
 * A $ amount or a % locks a customer line (manualOverride), the same as typing a price or % in the
 * add-item modal; "follow global" unlocks it and values it at the trade's %. Store lines carry no
 * customer pricing, so only their value is sent and their metadata (basket code etc.) is untouched.
 */
export function buildLinePriceUpdate(
  line: { side: 'store' | 'customer'; metadata?: Record<string, unknown> | null },
  edit: LinePriceEdit,
): LinePriceUpdate | null {
  if (line.side === 'store') {
    return edit.kind === 'amount' ? { valueCents: Math.max(0, Math.round(edit.valueCents)) } : null
  }

  const metadata = isRecord(line.metadata) ? line.metadata : {}
  const pricing = readLinePricing(metadata)
  const market = readLineMarketCents(metadata)
  const withPricing = (valueCents: number, next: Record<string, unknown>): LinePriceUpdate => ({
    valueCents,
    metadata: { ...metadata, pricing: compactPricing(next) },
  })

  if (edit.kind === 'amount') {
    return withPricing(Math.max(0, Math.round(edit.valueCents)), {
      ...pricing,
      percentOfMarket: undefined,
      manualOverride: true,
      floorApplied: undefined,
    })
  }
  if (market <= 0) return null
  if (edit.kind === 'percent') {
    const pct = Math.max(0, Math.min(CUSTOMER_PERCENT_UI_MAX, edit.percent))
    return withPricing(customerLineValueFromBulkPercent(market, pct), {
      ...pricing,
      percentOfMarket: pct,
      manualOverride: true,
      floorApplied: undefined,
    })
  }
  // With no trade-level % a following line sits at market, as the add-item modal does (and the
  // floor, which only applies with a %, stays off).
  if (edit.globalPercent === null) {
    return withPricing(customerLineValueFromBulkPercent(market, 100), {
      ...pricing,
      percentOfMarket: undefined,
      manualOverride: undefined,
      floorApplied: undefined,
    })
  }
  const priced = priceFollowingCustomerLine(market, edit.globalPercent, edit.floor)
  return withPricing(priced.unitValueCents, {
    ...pricing,
    percentOfMarket: edit.globalPercent,
    manualOverride: undefined,
    floorApplied: priced.floorApplied ? true : undefined,
  })
}
